import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Dumbbell, LogOut, Plus, Trash2, Edit2, Image as ImageIcon, Tag, Gift, Save, X, Upload, 
  LayoutDashboard, Percent, Eye, EyeOff, Search, Filter, ChevronDown, Check, Moon, Sun,
  Globe, Video, Play, Settings, Lock, MessageSquare, Bell
} from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { 
  offersService, 
  galleryService, 
  promotionsService, 
  messagesService, 
  landingPhotosService, 
  heroVideoService, 
  siteSettingsService, 
  adminCredentialsService 
} from '../services/database.js'
import { uploadToR2, deleteFromR2 } from '../services/storage.js'
import { sendPushNotification } from '../services/notifications.js'

function Admin() {
  const { theme, toggleTheme } = useTheme()
  const { language, toggleLanguage, t } = useLanguage()
  
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [loginData, setLoginData] = useState({ username: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  
  // Admin credentials - now using Supabase
  const [adminCredentials, setAdminCredentials] = useState({ username: 'admin', password: 'admin123' })
  const [credentialsLoaded, setCredentialsLoaded] = useState(false)
  
  // Modals state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [editingItem, setEditingItem] = useState(null)
  
  // Password change modal
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  
  // Data state - now using Supabase
  const [offers, setOffers] = useState([])
  const [gallery, setGallery] = useState([])
  const [promotions, setPromotions] = useState([])
  const [siteSettings, setSiteSettings] = useState({
    gymName: 'OXYGEN GYM',
    phone: '+966 50 000 0000',
    email: 'info@oxygengym.com',
    address: language === 'ar' ? 'الرياض، المملكة العربية السعودية' : 'Riyadh, Saudi Arabia',
    workingHours: '24/7',
    whatsappNumber: '966500000000',
    socialMedia: {
      instagram: '@oxygengym',
      twitter: '@oxygengym',
      snapchat: 'oxygengym'
    }
  })
  const [landingPhotos, setLandingPhotos] = useState([])
  const [heroVideo, setHeroVideo] = useState(null)
  const [messages, setMessages] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)

  // Load data from Supabase on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          offersData,
          galleryData,
          promotionsData,
          settingsData,
          landingPhotosData,
          heroVideoData,
          messagesData,
          credentialsData
        ] = await Promise.all([
          offersService.getAll(),
          galleryService.getAll(),
          promotionsService.getAll(),
          siteSettingsService.getAll(),
          landingPhotosService.getAll(),
          heroVideoService.getAll(),
          messagesService.getAll(),
          adminCredentialsService.getAll()
        ])

        setOffers(offersData || [])
        setGallery(galleryData || [])
        setPromotions(promotionsData || [])
        setLandingPhotos(landingPhotosData || [])
        setMessages(messagesData || [])
        
        if (heroVideoData && heroVideoData.length > 0) {
          setHeroVideo(heroVideoData[0])
        }
        
        if (settingsData && settingsData.length > 0) {
          const loadedSettings = settingsData[0]
          // Map snake_case from database to camelCase for frontend
          setSiteSettings({
            id: loadedSettings.id,
            gymName: loadedSettings.gym_name || 'OXYGEN GYM',
            phone: loadedSettings.phone || '+966 50 000 0000',
            email: loadedSettings.email || 'info@oxygengym.com',
            address: loadedSettings.address || (language === 'ar' ? 'الرياض، المملكة العربية السعودية' : 'Riyadh, Saudi Arabia'),
            workingHours: loadedSettings.working_hours || '24/7',
            whatsappNumber: loadedSettings.whatsapp_number || '966500000000',
            socialMedia: loadedSettings.social_media || {
              instagram: '@oxygengym',
              twitter: '@oxygengym',
              snapchat: 'oxygengym'
            }
          })
        }
        
        if (credentialsData && credentialsData.length > 0) {
          setAdminCredentials(credentialsData[0])
          setCredentialsLoaded(true)
        }
        
        setDataLoaded(true)
      } catch (error) {
        console.error('Error loading data:', error)
        setDataLoaded(true)
      }
    }

    loadData()
  }, [])

  // Send push notification when new data is added
  const notifyNewContent = async (type, title) => {
    try {
      await sendPushNotification(
        'New Content Added',
        `${title} has been added to Oxygen Gym`,
        { type, title }
      )
    } catch (error) {
      console.error('Error sending notification:', error)
    }
  }

  // Form states
  const [offerForm, setOfferForm] = useState({ name: '', price: '', duration: '', features: '', active: true })
  const [galleryForm, setGalleryForm] = useState({ title: '', category: '', image: null })
  const [promotionForm, setPromotionForm] = useState({ title: '', description: '', discount: '', code: '', active: true })
  const [settingsForm, setSettingsForm] = useState(siteSettings)
  const [landingPhotoForm, setLandingPhotoForm] = useState({ title: '', image: null })

  // File input refs
  const fileInputRef = useRef(null)
  const videoInputRef = useRef(null)
  const landingPhotoInputRef = useRef(null)

  const handleLogin = (e) => {
    e.preventDefault()
    if (loginData.username === adminCredentials.username && loginData.password === adminCredentials.password) {
      setIsLoggedIn(true)
    } else {
      alert(t('invalidCredentials'))
    }
  }

  const handleLogout = () => {
    setIsLoggedIn(false)
    setLoginData({ username: '', password: '' })
  }

  // Password change handler
  const handleChangePassword = () => {
    if (passwordForm.currentPassword !== adminCredentials.password) {
      alert(language === 'ar' ? 'كلمة المرور الحالية غير صحيحة' : 'Current password is incorrect')
      return
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert(t('passwordsDontMatch'))
      return
    }
    if (passwordForm.newPassword.length < 6) {
      alert(language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters')
      return
    }
    
    setAdminCredentials({
      ...adminCredentials,
      password: passwordForm.newPassword
    })
    alert(t('passwordChanged'))
    setPasswordModalOpen(false)
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
  }

  // Modal handlers
  const openModal = (type, item = null) => {
    setModalType(type)
      setEditingItem(item)
      
    if (type === 'offer' && item) {
      setOfferForm({
        name: item.name,
        price: item.price,
        duration: item.duration,
        features: Array.isArray(item.features) ? item.features.join('\n') : item.features,
        active: item.active
      })
    } else if (type === 'gallery' && item) {
      setGalleryForm({
        title: item.title,
        category: item.category,
        image: item.image_url
      })
    } else if (type === 'promotion' && item) {
      setPromotionForm({
        title: item.title,
        description: item.description,
        discount: item.discount,
        code: item.code,
        active: item.active
      })
    } else if (type === 'settings') {
      setSettingsForm(siteSettings)
    } else {
      setOfferForm({ name: '', price: '', duration: '', features: '', active: true })
      setGalleryForm({ title: '', category: '', image: null })
      setPromotionForm({ title: '', description: '', discount: '', code: '', active: true })
    }
    
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalType(null)
    setEditingItem(null)
  }

  // CRUD Operations
  const saveOffer = async () => {
    try {
      const features = offerForm.features.split('\n').filter(f => f.trim())
      
      if (editingItem) {
        const updatedOffer = await offersService.update(editingItem.id, {
          name: offerForm.name,
          price: offerForm.price,
          duration: offerForm.duration,
          features,
          active: offerForm.active
        })
        setOffers(offers.map(o => o.id === editingItem.id ? updatedOffer : o))
      } else {
        const newOffer = await offersService.create({
          name: offerForm.name,
          price: offerForm.price,
          duration: offerForm.duration,
          features,
          active: offerForm.active
        })
        setOffers([...offers, newOffer])
        await notifyNewContent('offer', newOffer.name)
      }
      closeModal()
    } catch (error) {
      console.error('Error saving offer:', error)
      alert(language === 'ar' ? 'حدث خطأ أثناء حفظ العرض' : 'Error saving offer')
    }
  }

  const deleteOffer = async (id) => {
    if (confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا العرض؟' : 'Are you sure you want to delete this offer?')) {
      try {
        await offersService.delete(id)
        setOffers(offers.filter(o => o.id !== id))
      } catch (error) {
        console.error('Error deleting offer:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء حذف العرض' : 'Error deleting offer')
      }
    }
  }

  const toggleOfferStatus = async (id) => {
    try {
      await offersService.toggleActive(id)
      setOffers(offers.map(o => o.id === id ? { ...o, active: !o.active } : o))
    } catch (error) {
      console.error('Error toggling offer status:', error)
    }
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (file) {
      try {
        const uploadResult = await uploadToR2(file, 'gallery')
        setGalleryForm({ ...galleryForm, image: uploadResult.url })
      } catch (error) {
        console.error('Error uploading image:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء رفع الصورة' : 'Error uploading image')
      }
    }
  }

  const saveGalleryItem = async () => {
    try {
      if (editingItem) {
        const updatedItem = await galleryService.update(editingItem.id, {
          title: galleryForm.title,
          category: galleryForm.category,
          image_url: galleryForm.image
        })
        setGallery(gallery.map(g => g.id === editingItem.id ? updatedItem : g))
      } else {
        const newItem = await galleryService.create({
          title: galleryForm.title,
          category: galleryForm.category,
          image_url: galleryForm.image
        })
        setGallery([...gallery, newItem])
        await notifyNewContent('gallery', newItem.title)
      }
      closeModal()
    } catch (error) {
      console.error('Error saving gallery item:', error)
      alert(language === 'ar' ? 'حدث خطأ أثناء حفظ الصورة' : 'Error saving image')
    }
  }

  const deleteGalleryItem = async (id) => {
    if (confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الصورة؟' : 'Are you sure you want to delete this image?')) {
      try {
        const item = gallery.find(g => g.id === id)
        if (item?.image_url) {
          const path = item.image_url.split('/').pop()
          await deleteFromR2(`gallery/${path}`)
        }
        await galleryService.delete(id)
        setGallery(gallery.filter(g => g.id !== id))
      } catch (error) {
        console.error('Error deleting gallery item:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء حذف الصورة' : 'Error deleting image')
      }
    }
  }

  const savePromotion = async () => {
    try {
      if (editingItem) {
        const updatedPromotion = await promotionsService.update(editingItem.id, {
          title: promotionForm.title,
          description: promotionForm.description,
          discount: promotionForm.discount,
          code: promotionForm.code,
          active: promotionForm.active
        })
        setPromotions(promotions.map(p => p.id === editingItem.id ? updatedPromotion : p))
      } else {
        const newPromotion = await promotionsService.create({
          title: promotionForm.title,
          description: promotionForm.description,
          discount: promotionForm.discount,
          code: promotionForm.code,
          active: promotionForm.active
        })
        setPromotions([...promotions, newPromotion])
        await notifyNewContent('promotion', newPromotion.title)
      }
      closeModal()
    } catch (error) {
      console.error('Error saving promotion:', error)
      alert(language === 'ar' ? 'حدث خطأ أثناء حفظ العرض الترويجي' : 'Error saving promotion')
    }
  }

  const deletePromotion = async (id) => {
    if (confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا العرض الترويجي؟' : 'Are you sure you want to delete this promotion?')) {
      try {
        await promotionsService.delete(id)
        setPromotions(promotions.filter(p => p.id !== id))
      } catch (error) {
        console.error('Error deleting promotion:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء حذف العرض الترويجي' : 'Error deleting promotion')
      }
    }
  }

  const togglePromotionStatus = async (id) => {
    try {
      await promotionsService.toggleActive(id)
      setPromotions(promotions.map(p => p.id === id ? { ...p, active: !p.active } : p))
    } catch (error) {
      console.error('Error toggling promotion status:', error)
    }
  }

  const saveSettings = async () => {
    try {
      // Map camelCase field names to snake_case for database
      const dbSettings = {
        gym_name: settingsForm.gymName,
        phone: settingsForm.phone,
        email: settingsForm.email,
        address: settingsForm.address,
        working_hours: settingsForm.workingHours,
        whatsapp_number: settingsForm.whatsappNumber,
        social_media: settingsForm.socialMedia
      }
      
      if (siteSettings.id) {
        const updatedSettings = await siteSettingsService.update(siteSettings.id, dbSettings)
        setSiteSettings(updatedSettings)
      } else {
        const newSettings = await siteSettingsService.create(dbSettings)
        setSiteSettings(newSettings)
      }
      closeModal()
    } catch (error) {
      console.error('Error saving settings:', error)
      alert(language === 'ar' ? 'حدث خطأ أثناء حفظ الإعدادات' : 'Error saving settings')
    }
  }

  // Landing page photos handlers
  const handleLandingPhotoUpload = async (e) => {
    const file = e.target.files[0]
    if (file) {
      try {
        const uploadResult = await uploadToR2(file, 'landing')
        setLandingPhotoForm({ ...landingPhotoForm, image: uploadResult.url })
      } catch (error) {
        console.error('Error uploading landing photo:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء رفع الصورة' : 'Error uploading photo')
      }
    }
  }

  const saveLandingPhoto = async () => {
    try {
      const newPhoto = await landingPhotosService.create({
        title: landingPhotoForm.title,
        image_url: landingPhotoForm.image
      })
      setLandingPhotos([...landingPhotos, newPhoto])
      setLandingPhotoForm({ title: '', image: null })
      await notifyNewContent('landing', newPhoto.title)
    } catch (error) {
      console.error('Error saving landing photo:', error)
      alert(language === 'ar' ? 'حدث خطأ أثناء حفظ الصورة' : 'Error saving photo')
    }
  }

  const deleteLandingPhoto = async (id) => {
    if (confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الصورة؟' : 'Are you sure you want to delete this photo?')) {
      try {
        const photo = landingPhotos.find(p => p.id === id)
        if (photo?.image_url) {
          const path = photo.image_url.split('/').pop()
          await deleteFromR2(`landing/${path}`)
        }
        await landingPhotosService.delete(id)
        setLandingPhotos(landingPhotos.filter(p => p.id !== id))
      } catch (error) {
        console.error('Error deleting landing photo:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء حذف الصورة' : 'Error deleting photo')
      }
    }
  }

  // Hero video handlers
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0]
    if (file) {
      try {
        const uploadResult = await uploadToR2(file, 'videos')
        const newVideo = {
          name: file.name,
          video_url: uploadResult.url
        }
        
        if (heroVideo?.id) {
          const updatedVideo = await heroVideoService.update(heroVideo.id, newVideo)
          setHeroVideo(updatedVideo)
        } else {
          const createdVideo = await heroVideoService.create(newVideo)
          setHeroVideo(createdVideo)
        }
      } catch (error) {
        console.error('Error uploading video:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء رفع الفيديو' : 'Error uploading video')
      }
    }
  }

  const deleteHeroVideo = async () => {
    if (confirm(language === 'ar' ? 'هل أنت متأكد من حذف الفيديو؟' : 'Are you sure you want to delete the video?')) {
      try {
        if (heroVideo?.video_url) {
          const path = heroVideo.video_url.split('/').pop()
          await deleteFromR2(`videos/${path}`)
        }
        if (heroVideo?.id) {
          await heroVideoService.delete(heroVideo.id)
        }
        setHeroVideo(null)
      } catch (error) {
        console.error('Error deleting video:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء حذف الفيديو' : 'Error deleting video')
      }
    }
  }

  // Messages handlers
  const deleteMessage = async (id) => {
    if (confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الرسالة؟' : 'Are you sure you want to delete this message?')) {
      try {
        await messagesService.delete(id)
        setMessages(messages.filter(m => m.id !== id))
      } catch (error) {
        console.error('Error deleting message:', error)
        alert(language === 'ar' ? 'حدث خطأ أثناء حذف الرسالة' : 'Error deleting message')
      }
    }
  }

  const markMessageAsRead = async (id) => {
    try {
      await messagesService.update(id, { read: true })
      setMessages(messages.map(m => m.id === id ? { ...m, read: true } : m))
    } catch (error) {
      console.error('Error marking message as read:', error)
    }
  }

  // Dashboard stats
  const stats = [
    { label: language === 'ar' ? 'العروض النشطة' : 'Active Offers', value: offers.filter(o => o.active).length, icon: Tag, color: 'yellow' },
    { label: language === 'ar' ? 'صور المعرض' : 'Gallery Images', value: gallery.length, icon: ImageIcon, color: 'blue' },
    { label: language === 'ar' ? 'العروض الترويجية' : 'Promotions', value: promotions.filter(p => p.active).length, icon: Gift, color: 'green' },
    { label: language === 'ar' ? 'الرسائل الجديدة' : 'New Messages', value: messages.filter(m => !m.read).length, icon: MessageSquare, color: 'red' },
  ]

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center p-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="glass-effect rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Dumbbell className="w-8 h-8 text-black" />
              </div>
              <h1 className="text-2xl font-bold text-white">{t('admin')}</h1>
              <p className="text-gray-400 mt-2">Oxygen Gym</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-gray-400 mb-2 text-sm">{t('username')}</label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-gray-400 mb-2 text-sm">{t('password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all duration-300"
              >
                {t('login')}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dark" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Top Navigation */}
      <nav className="glass-effect border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                <Dumbbell className="w-6 h-6 text-black" />
              </div>
              <span className="text-xl font-bold text-gradient">OXYGEN Admin</span>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl bg-white/5 text-gray-400 hover:text-yellow-400 transition-colors"
                title={theme === 'dark' ? 'Night Mode' : 'Dark Mode'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="p-2 rounded-xl bg-white/5 text-gray-400 hover:text-yellow-400 transition-colors"
                title={language === 'ar' ? 'English' : 'العربية'}
              >
                <Globe className="w-5 h-5" />
              </button>
              
              {/* Change Password */}
              <button
                onClick={() => setPasswordModalOpen(true)}
                className="p-2 rounded-xl bg-white/5 text-gray-400 hover:text-yellow-400 transition-colors"
                title={t('changePassword')}
              >
                <Lock className="w-5 h-5" />
              </button>
              
              {/* Logout */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen glass-effect border-l border-white/10 hidden md:block">
          <div className="p-6">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === 'dashboard' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <LayoutDashboard className="w-5 h-5" />
                <span>{t('dashboard')}</span>
              </button>
              <button
                onClick={() => setActiveTab('offers')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === 'offers' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Tag className="w-5 h-5" />
                <span>{t('offersAndSubscriptions')}</span>
              </button>
              <button
                onClick={() => setActiveTab('gallery')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === 'gallery' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <ImageIcon className="w-5 h-5" />
                <span>{t('photoGallery')}</span>
              </button>
              <button
                onClick={() => setActiveTab('landing')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === 'landing' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Play className="w-5 h-5" />
                <span>{language === 'ar' ? 'الصفحة الرئيسية' : 'Landing Page'}</span>
              </button>
              <button
                onClick={() => setActiveTab('promotions')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === 'promotions' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Gift className="w-5 h-5" />
                <span>{t('promotions')}</span>
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === 'messages' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                <span>{t('messages')}</span>
                {messages.filter(m => !m.read).length > 0 && (
                  <span className="mr-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {messages.filter(m => !m.read).length}
                  </span>
                )}
              </button>
              <button
                onClick={() => openModal('settings')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  modalType === 'settings' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Settings className="w-5 h-5" />
                <span>{t('siteSettings')}</span>
              </button>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="text-3xl font-bold text-white mb-8">{t('dashboard')}</h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    className="glass-effect rounded-2xl p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      stat.color === 'yellow' ? 'bg-yellow-400/20' :
                      stat.color === 'blue' ? 'bg-blue-400/20' :
                      stat.color === 'green' ? 'bg-green-400/20' :
                      'bg-red-400/20'
                    }`}>
                      <stat.icon className={`w-6 h-6 ${
                        stat.color === 'yellow' ? 'text-yellow-400' :
                        stat.color === 'blue' ? 'text-blue-400' :
                        stat.color === 'green' ? 'text-green-400' :
                        'text-red-400'
                      }`} />
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-gray-400">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                <div className="glass-effect rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-white mb-6">{language === 'ar' ? 'آخر العروض' : 'Latest Offers'}</h3>
                  <div className="space-y-4">
                    {offers.slice(0, 3).map(offer => (
                      <div key={offer.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                        <div>
                          <div className="font-bold text-white">{offer.name}</div>
                          <div className="text-sm text-gray-400">{offer.price} {t('sar')} / {offer.duration}</div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          offer.active ? 'bg-green-400/20 text-green-400' : 'bg-gray-400/20 text-gray-400'
                        }`}>
                          {offer.active ? t('active') : t('inactive')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="glass-effect rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-white mb-6">{language === 'ar' ? 'آخر الرسائل' : 'Latest Messages'}</h3>
                  <div className="space-y-4">
                    {messages.slice(0, 3).map(message => (
                      <div key={message.id} className={`p-4 rounded-xl ${message.read ? 'bg-white/5' : 'bg-yellow-400/10'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-white">{message.name}</span>
                          <span className="text-xs text-gray-400">{new Date(message.date).toLocaleDateString()}</span>
                        </div>
                        <p className="text-gray-400 text-sm line-clamp-2">{message.message}</p>
                      </div>
                    ))}
                    {messages.length === 0 && (
                      <p className="text-gray-500 text-center py-8">{language === 'ar' ? 'لا توجد رسائل' : 'No messages yet'}</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Offers Tab */}
          {activeTab === 'offers' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">{t('offersAndSubscriptions')}</h2>
                <button 
                  onClick={() => openModal('offer')}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('addOffer')}</span>
                </button>
              </div>

              <div className="grid gap-4">
                {offers.map((offer) => (
                  <div key={offer.id} className="glass-effect rounded-xl p-6 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-white">{offer.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          offer.active ? 'bg-green-400/20 text-green-400' : 'bg-gray-400/20 text-gray-400'
                        }`}>
                          {offer.active ? t('active') : t('inactive')}
                        </span>
                      </div>
                      <p className="text-gray-400">{offer.price} {t('sar')} / {offer.duration}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Array.isArray(offer.features) && offer.features.map((feature, idx) => (
                          <span key={idx} className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => toggleOfferStatus(offer.id)}
                        className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-yellow-400/20 hover:text-yellow-400 transition-colors"
                        title={offer.active ? 'تعطيل' : 'تفعيل'}
                      >
                        {offer.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={() => openModal('offer', offer)}
                        className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-yellow-400/20 hover:text-yellow-400 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => deleteOffer(offer.id)}
                        className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">{t('photoGallery')}</h2>
                <button 
                  onClick={() => openModal('gallery')}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                >
                  <Upload className="w-4 h-4" />
                  <span>{t('uploadImage')}</span>
                </button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gallery.map((item) => (
                  <div key={item.id} className="glass-effect rounded-xl overflow-hidden">
                    <div className="aspect-square bg-gray-800 flex items-center justify-center relative overflow-hidden">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-12 h-12 text-gray-600" />
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-white">{item.title}</h3>
                      <p className="text-sm text-gray-400">{item.category}</p>
                      <div className="flex items-center gap-2 mt-4">
                        <button 
                          onClick={() => openModal('gallery', item)}
                          className="flex-1 py-2 rounded-lg bg-white/5 text-gray-400 hover:bg-yellow-400/20 hover:text-yellow-400 transition-colors text-sm"
                        >
                          {t('edit')}
                        </button>
                        <button 
                          onClick={() => deleteGalleryItem(item.id)}
                          className="flex-1 py-2 rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors text-sm"
                        >
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Landing Page Tab (Video + Photos) */}
          {activeTab === 'landing' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              {/* Hero Video Section */}
              <div>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold text-white">{t('heroVideo')}</h2>
                  <div className="flex gap-2">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                    />
                    <button 
                      onClick={() => videoInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                    >
                      <Video className="w-4 h-4" />
                      <span>{t('uploadVideo')}</span>
                    </button>
                  </div>
                </div>

                {heroVideo ? (
                  <div className="glass-effect rounded-xl p-6">
                    <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden mb-4">
                      <video 
                        src={heroVideo.video_url} 
                        controls 
                        className="w-full h-full"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">{heroVideo.name}</span>
                      <button 
                        onClick={deleteHeroVideo}
                        className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="glass-effect rounded-xl p-12 text-center">
                    <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">{language === 'ar' ? 'لا يوجد فيديو مرفوع' : 'No video uploaded'}</p>
                  </div>
                )}
              </div>

              {/* Landing Page Photos Section */}
              <div>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold text-white">{t('landingPagePhotos')}</h2>
                  <div className="flex gap-2">
                    <input
                      ref={landingPhotoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLandingPhotoUpload}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Upload Form */}
                <div className="glass-effect rounded-xl p-6 mb-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'عنوان الصورة' : 'Photo Title'}</label>
                      <input
                        type="text"
                        value={landingPhotoForm.title}
                        onChange={(e) => setLandingPhotoForm({ ...landingPhotoForm, title: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                        placeholder={language === 'ar' ? 'مثال: صورة البطل' : 'e.g. Hero Image'}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2 text-sm">{t('uploadHeroImage')}</label>
                      <div
                        onClick={() => landingPhotoInputRef.current?.click()}
                        className="w-full h-[54px] rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-yellow-400/50 hover:bg-white/5 transition-all"
                      >
                        {landingPhotoForm.image ? (
                          <span className="text-green-400">{language === 'ar' ? 'تم اختيار الصورة ✓' : 'Image selected ✓'}</span>
                        ) : (
                          <span className="text-gray-400 flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            {language === 'ar' ? 'اضغط لاختيار صورة' : 'Click to select image'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={saveLandingPhoto}
                    disabled={!landingPhotoForm.title || !landingPhotoForm.image}
                    className="mt-4 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('save')}
                  </button>
                </div>

                {/* Photos Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {landingPhotos.map((photo) => (
                    <div key={photo.id} className="glass-effect rounded-xl overflow-hidden">
                      <div className="aspect-video bg-gray-800 relative">
                        <img src={photo.image_url} alt={photo.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4 flex items-center justify-between">
                        <span className="text-white font-medium">{photo.title}</span>
                        <button 
                          onClick={() => deleteLandingPhoto(photo.id)}
                          className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {landingPhotos.length === 0 && (
                  <div className="glass-effect rounded-xl p-12 text-center">
                    <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">{language === 'ar' ? 'لا توجد صور مرفوعة' : 'No photos uploaded'}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Promotions Tab */}
          {activeTab === 'promotions' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">{t('promotions')}</h2>
                <button 
                  onClick={() => openModal('promotion')}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('addPromotion')}</span>
                </button>
              </div>

              <div className="grid gap-4">
                {promotions.length === 0 ? (
                  <div className="glass-effect rounded-xl p-8 text-center">
                    <Gift className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">{language === 'ar' ? 'لا توجد عروض ترويجية حالياً' : 'No promotions yet'}</h3>
                    <p className="text-gray-400">{language === 'ar' ? 'يمكنك إضافة عروض ترويجية جديدة هنا' : 'You can add new promotions here'}</p>
                  </div>
                ) : (
                  promotions.map((promo) => (
                    <div key={promo.id} className="glass-effect rounded-xl p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-white">{promo.title}</h3>
                            <span className="px-3 py-1 rounded-full bg-yellow-400/20 text-yellow-400 text-sm font-bold">
                              {promo.discount}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              promo.active ? 'bg-green-400/20 text-green-400' : 'bg-gray-400/20 text-gray-400'
                            }`}>
                              {promo.active ? t('active') : t('inactive')}
                            </span>
                          </div>
                          <p className="text-gray-400 mb-2">{promo.description}</p>
                          <div className="text-sm text-yellow-400">{language === 'ar' ? 'الكود:' : 'Code:'} <strong>{promo.code}</strong></div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => togglePromotionStatus(promo.id)}
                            className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-yellow-400/20 hover:text-yellow-400 transition-colors"
                          >
                            {promo.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button 
                            onClick={() => openModal('promotion', promo)}
                            className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-yellow-400/20 hover:text-yellow-400 transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => deletePromotion(promo.id)}
                            className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">{t('messages')}</h2>
                <span className="text-gray-400">
                  {messages.filter(m => !m.read).length} {language === 'ar' ? 'رسائل جديدة' : 'new messages'}
                </span>
              </div>

              <div className="grid gap-4">
                {messages.length === 0 ? (
                  <div className="glass-effect rounded-xl p-8 text-center">
                    <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">{language === 'ar' ? 'لا توجد رسائل' : 'No messages'}</h3>
                    <p className="text-gray-400">{language === 'ar' ? 'ستظهر رسائل المستخدمين هنا' : 'User messages will appear here'}</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <motion.div 
                      key={message.id} 
                      className={`glass-effect rounded-xl p-6 ${!message.read ? 'border-r-4 border-yellow-400' : ''}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-bold text-white text-lg">{message.name}</h3>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                            <span>{message.email}</span>
                            <span>{message.phone}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!message.read && (
                            <button 
                              onClick={() => markMessageAsRead(message.id)}
                              className="p-2 rounded-lg bg-yellow-400/20 text-yellow-400 hover:bg-yellow-400/30 transition-colors"
                              title={language === 'ar' ? 'تحديد كمقروء' : 'Mark as read'}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          <button 
                            onClick={() => deleteMessage(message.id)}
                            className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-300 bg-white/5 rounded-xl p-4">{message.message}</p>
                      <div className="mt-4 text-xs text-gray-500">
                        {new Date(message.date).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </main>
      </div>

      {/* Main Modals */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="glass-effect rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">
                  {modalType === 'offer' && (editingItem ? (language === 'ar' ? 'تعديل عرض' : 'Edit Offer') : (language === 'ar' ? 'إضافة عرض جديد' : 'Add New Offer'))}
                  {modalType === 'gallery' && (editingItem ? (language === 'ar' ? 'تعديل صورة' : 'Edit Image') : (language === 'ar' ? 'رفع صورة جديدة' : 'Upload New Image'))}
                  {modalType === 'promotion' && (editingItem ? (language === 'ar' ? 'تعديل عرض ترويجي' : 'Edit Promotion') : (language === 'ar' ? 'إضافة عرض ترويجي' : 'Add New Promotion'))}
                  {modalType === 'settings' && t('siteSettings')}
                </h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Offer Form */}
              {modalType === 'offer' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'اسم العرض' : 'Offer Name'}</label>
                    <input
                      type="text"
                      value={offerForm.name}
                      onChange={(e) => setOfferForm({ ...offerForm, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder={language === 'ar' ? 'مثال: اشتراك شهري' : 'e.g. Monthly Subscription'}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'السعر' : 'Price'}</label>
                      <input
                        type="text"
                        value={offerForm.price}
                        onChange={(e) => setOfferForm({ ...offerForm, price: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                        placeholder="199"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'المدة' : 'Duration'}</label>
                      <input
                        type="text"
                        value={offerForm.duration}
                        onChange={(e) => setOfferForm({ ...offerForm, duration: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                        placeholder={language === 'ar' ? 'شهر' : 'Month'}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'المميزات (كل سطر ميزة)' : 'Features (one per line)'}</label>
                    <textarea
                      value={offerForm.features}
                      onChange={(e) => setOfferForm({ ...offerForm, features: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors h-32"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="offerActive"
                      checked={offerForm.active}
                      onChange={(e) => setOfferForm({ ...offerForm, active: e.target.checked })}
                      className="w-5 h-5 rounded border-white/10 bg-white/5 text-yellow-400 focus:ring-yellow-400"
                    />
                    <label htmlFor="offerActive" className="text-white">{t('active')}</label>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={saveOffer}
                      className="flex-1 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                    >
                      {t('save')}
                    </button>
                    <button
                      onClick={closeModal}
                      className="flex-1 py-3 bg-white/5 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* Gallery Form */}
              {modalType === 'gallery' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'عنوان الصورة' : 'Image Title'}</label>
                    <input
                      type="text"
                      value={galleryForm.title}
                      onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'التصنيف' : 'Category'}</label>
                    <select
                      value={galleryForm.category}
                      onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                    >
                      <option value="">{language === 'ar' ? 'اختر التصنيف' : 'Select Category'}</option>
                      <option value={language === 'ar' ? 'معدات' : 'Equipment'}>{language === 'ar' ? 'معدات' : 'Equipment'}</option>
                      <option value={language === 'ar' ? 'كارديو' : 'Cardio'}>{language === 'ar' ? 'كارديو' : 'Cardio'}</option>
                      <option value={language === 'ar' ? 'مجموعات' : 'Groups'}>{language === 'ar' ? 'مجموعات' : 'Groups'}</option>
                      <option value={language === 'ar' ? 'مدربين' : 'Trainers'}>{language === 'ar' ? 'مدربين' : 'Trainers'}</option>
                      <option value={language === 'ar' ? 'مرافق' : 'Facilities'}>{language === 'ar' ? 'مرافق' : 'Facilities'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{t('uploadImage')}</label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-48 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-400/50 hover:bg-white/5 transition-all"
                    >
                      {galleryForm.image ? (
                        <img src={galleryForm.image} alt="Preview" className="h-full object-contain" />
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-gray-500 mb-2" />
                          <span className="text-gray-400">{language === 'ar' ? 'اضغط لرفع صورة' : 'Click to upload image'}</span>
                        </>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={saveGalleryItem}
                      className="flex-1 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                    >
                      {t('save')}
                    </button>
                    <button
                      onClick={closeModal}
                      className="flex-1 py-3 bg-white/5 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* Promotion Form */}
              {modalType === 'promotion' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'عنوان العرض' : 'Promotion Title'}</label>
                    <input
                      type="text"
                      value={promotionForm.title}
                      onChange={(e) => setPromotionForm({ ...promotionForm, title: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'الوصف' : 'Description'}</label>
                    <textarea
                      value={promotionForm.description}
                      onChange={(e) => setPromotionForm({ ...promotionForm, description: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors h-24"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'نسبة الخصم' : 'Discount'}</label>
                      <input
                        type="text"
                        value={promotionForm.discount}
                        onChange={(e) => setPromotionForm({ ...promotionForm, discount: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                        placeholder="20%"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2 text-sm">{language === 'ar' ? 'كود الخصم' : 'Promo Code'}</label>
                      <input
                        type="text"
                        value={promotionForm.code}
                        onChange={(e) => setPromotionForm({ ...promotionForm, code: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                        placeholder="START20"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="promoActive"
                      checked={promotionForm.active}
                      onChange={(e) => setPromotionForm({ ...promotionForm, active: e.target.checked })}
                      className="w-5 h-5 rounded border-white/10 bg-white/5 text-yellow-400 focus:ring-yellow-400"
                    />
                    <label htmlFor="promoActive" className="text-white">{t('active')}</label>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={savePromotion}
                      className="flex-1 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                    >
                      {t('save')}
                    </button>
                    <button
                      onClick={closeModal}
                      className="flex-1 py-3 bg-white/5 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* Settings Form */}
              {modalType === 'settings' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{t('phone')}</label>
                    <input
                      type="text"
                      value={settingsForm.phone}
                      onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{t('email')}</label>
                    <input
                      type="email"
                      value={settingsForm.email}
                      onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{t('address')}</label>
                    <input
                      type="text"
                      value={settingsForm.address}
                      onChange={(e) => setSettingsForm({ ...settingsForm, address: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">{t('workingHours')}</label>
                    <input
                      type="text"
                      value={settingsForm.workingHours}
                      onChange={(e) => setSettingsForm({ ...settingsForm, workingHours: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">Instagram URL</label>
                    <input
                      type="text"
                      value={settingsForm.socialMedia?.instagram || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, socialMedia: { ...settingsForm.socialMedia, instagram: e.target.value } })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="https://instagram.com/youraccount"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">Twitter / X URL</label>
                    <input
                      type="text"
                      value={settingsForm.socialMedia?.twitter || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, socialMedia: { ...settingsForm.socialMedia, twitter: e.target.value } })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="https://x.com/youraccount"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">Facebook URL</label>
                    <input
                      type="text"
                      value={settingsForm.socialMedia?.facebook || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, socialMedia: { ...settingsForm.socialMedia, facebook: e.target.value } })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="https://facebook.com/youraccount"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">Snapchat</label>
                    <input
                      type="text"
                      value={settingsForm.socialMedia?.snapchat || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, socialMedia: { ...settingsForm.socialMedia, snapchat: e.target.value } })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="youraccount"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">WhatsApp Number</label>
                    <input
                      type="text"
                      value={settingsForm.whatsappNumber || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, whatsappNumber: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="966501234567"
                    />
                    <p className="text-gray-500 text-xs mt-1">Enter with country code (e.g., 966501234567)</p>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={saveSettings}
                      className="flex-1 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                    >
                      {t('save')}
                    </button>
                    <button
                      onClick={closeModal}
                      className="flex-1 py-3 bg-white/5 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* Password Change Modal */}
        {passwordModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPasswordModalOpen(false)}
          >
            <motion.div
              className="glass-effect rounded-2xl p-6 w-full max-w-md"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">{t('changePassword')}</h3>
                <button onClick={() => setPasswordModalOpen(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">{t('currentPassword')}</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">{t('newPassword')}</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">{t('confirmPassword')}</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleChangePassword}
                    className="flex-1 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all"
                  >
                    {t('save')}
                  </button>
                  <button
                    onClick={() => setPasswordModalOpen(false)}
                    className="flex-1 py-3 bg-white/5 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Admin
